import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0414 from "../lesson-14/lesson";
import lesson0404 from "../lesson-04/lesson";
const linkLesson0404 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0404.id);
const linkLesson0114 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0414.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>In this Lesson, two electrodynamic microphones are modelled [1, 2, 3]: a moving coil pressure microphone and a bidirective ribbon microphone, the latter having a figure of eight directivity pattern. Their frequency response will be plotted and discussed. We will see how the acoustical, mechanical and electrical domain are coupled.
<h3 class=sectionHead>Moving coil microphone</h3>
<p>
   A schematic diagram of a moving coil microphone is provided in <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/figure-1.svg data-img-alt="Figure 1. Moving coil microphone.">Figure 1</a>. A cylindrical coil of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ℓ</mi>
   </math>
   is placed in a radial magnetic field
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>. The coil is rigidly attached to a membrane.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/15/theory/figure-1.svg alt="Figure 1. Moving coil microphone."><figcaption class=caption>Figure 1. Moving coil microphone.</figcaption></figure>
<p>The microphone behaviour can be described as follows:
<ul class=itemize1>
   <li class=itemize>
      a sound wave of pressure
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>f</mi>
            </mrow>
         </msub>
      </math>
      impinges on the front side of the diaphragm and creates a force
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>p</mi>
            </mrow>
            <mrow>
               <mi>f</mi>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>
      on the moving mass (coil + membrane assembly) which in turn vibrates with a velocity denoted
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mstyle mathvariant=bold>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mstyle>
      </math>,
   <li class=itemize>
      the movement produces an induced EMF
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>e</mi>
      </math>
      in the coil and then a current
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>I</mi>
      </math>
      when the coil is loaded by an electrical impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>, which corresponds to that of the device to which the microphone is connected,
   <li class=itemize>
      the current
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>I</mi>
      </math>
      creates in turn a Laplace force
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mstyle mathvariant=bold>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
         </mstyle>
      </math>
      directed upward which is in the opposed direction to the force created by the sound wave. Note that this behaviour fulfils Lenz’s law: The direction of current induced in a conductor is such that it creates a magnetic field that opposes the change that produced it.
</ul>
<p>To build a simplified model of the moving coil microphone, the following assumptions are used:
<ul class=itemize1>
   <li class=itemize>the presence of a protective grid is not taken into account,
   <li class=itemize>
      the diaphragm, considered to be a disc of radius
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>a</mi>
      </math>, and the coil are attached together and form a damped mechanical oscillator (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>), the acoustic radiation is included in these term,
   <li class=itemize>
      a low frequency approach is used here: the acoustic wavelength is supposed to be much larger than the radius of the membrane (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>λ</mi>
         <mo class=MathClass-rel>≫</mo>
         <mi>a</mi>
      </math>), thus diffraction and phase effects on the diaphragm are neglected (the acting force on the diaphragm is uniform ),
   <li class=itemize>the volume at the back is closed.
</ul>
<p>
   We can now write the behavioural equations starting with the electrical equation. The microphone is connected to a loading impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   on which the output voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   is measured. By writing Kirchhoff’s voltage law, we get
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>B</mi>
            <mi>ℓ</mi>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mi>I</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   is the current flowing in the coil,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   is the coil impedance and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
      <mi>ℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is the EMF caused by the coil vibration.<br class=newline>For the mechanical domain, the second law of Newton yields
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mi>ω</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   is the force due to the acoustic pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   which exists on the rear side of the membrane. Note that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
      <mi>ℓ</mi>
      <mi>I</mi>
   </math>
   corresponds to the Lorentz force. The above equation can be rewritten in a more compact form
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>I</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   is the mechanical impedance of the moving part of the microphone, which includes the coil, the diaphragm and the peripheral suspension.<br class=newline>The expression of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   can be obtained by using electroacoustical analogies to model the behaviour of the rear acoustical circuit. The volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   created by the back of the membrane flows into the magnetic circuit air gap and then into the back cavity. Using the lumped elements introduced in <a href="${linkLesson0404}" target="_blank">Lesson 4</a>, we can modelled the air gap by an acoustical mass
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   as it is open at both ends and the rear cavity by an acoustic compliance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. As the air gap is generally narrow, the associated viscous losses can be taken into account by a resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>. Finally, the pressure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   on the rear side of the diaphragm can be obtained with the following equation, which is analogous to the equation for the series RLC circuit
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                  </msub>
                  <mi>ω</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   is the acoustic impedance of the rear acoustic circuit. Finally, the three behavioural equations can be put into the form of the following coupled system:
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>l</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                           <mi>I</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mi>f</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <mi>I</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   This system of equations can also be represented by the equivalent electrical circuit depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/figure-2.svg data-img-alt="Figure 2. Equivalent electrical circuit of a moving coil microphone.">Figure 2</a>.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/15/theory/figure-2.svg alt="Figure 2. Equivalent electrical circuit of a moving coil microphone.">
      <figcaption class=caption>Figure 2. Equivalent electrical circuit of a moving coil microphone.</figcaption>
   </figure>
<p>
   Now that the behavioural equations have been expressed in the three domains of interest, let us express the sensitivity response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
   </math>. We can start by simplifying Equation (5), by bringing the equation from the acoustic domain into the mechanical domain using
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(6)
</table>
<p>The mechanical equation then becomes
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>f</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>I</mi>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>. Note that as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   are both RLC series circuits,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is also one. Eq. 5 can now be rewritten:
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>l</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                           <mi>I</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mi>f</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <mi>I</mi>
                        </mtd>
                     </mtr>
                     <mtr></mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   Let us start with the open circuit condition
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>∞</mi>
   </math>
   to get
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
   </math>, the open circuit sensitivity. In that case, as we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>B</mi>
      <mi>ℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>f</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>. Finally, we get
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                    <mrow>
                        <mi>V</mi>
                    </mrow>
                    <mrow>
                        <mi>d</mi>
                    </mrow>
                  </msub>
                  <msub>
                    <mrow>
                      <mi>S</mi>
                    </mrow>
                    <mrow>
                      <mi>d</mi>
                    </mrow>
                  </msub>
               </mrow>
               <mrow>
               <msub>
                <mrow>
                    <mi>Z</mi>
                </mrow>
                <mrow>
                    <mi>t</mi>
                </mrow>
              </msub>
              <msub>
                    <mrow>
                        <mi>V</mi>
                    </mrow>
                    <mrow>
                        <mi>d</mi>
                    </mrow>
                  </msub>
               </mrow>
            </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>B</mi>
               <mi>ℓ</mi>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>Z</mi>
                  </mrow>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   The sensitivity is flat if
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   does not depend on frequency, <em><span class=cmti-10>i.e.</span></em> for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>: the microphone response is controlled by its resistance. We can then define a nominal sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>B</mi>
            <mi>ℓ</mi>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   which is obtained in the microphone bandwidth. The relative sensitivity of the microphone in dB is then given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
                  <mi>B</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mn>0</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   The relative sensitivity of a typical moving coil microphone is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/figure-3.svg  data-img-alt="Figure 3. Relative sensitivity of a moving coil microphone.">Figure 3</a> as a function of frequency.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/15/theory/figure-3.svg alt="Figure 3. Relative sensitivity of a moving coil microphone."><figcaption class=caption>Figure 3. Relative sensitivity of a moving coil microphone.</figcaption></figure>
<p>
   The low and high cut-off frequencies at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>−</mo>
      <mn>3</mn>
   </math>
   dB can be obtained from the expression of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
   </math>
   and are respectively equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>h</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Note that as the bandwidth, given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>h</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>, is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, a large bandwidth requires a high
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   value.<br class=newline>To increase the sensitivity, the following parameters can be adjusted:
<ul class=itemize1>
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>B</mi>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ℓ</mi>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>
      should be raised. However, the increase of the surface and the coil length also increase the mass which in turn will lower
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mi>h</mi>
            </mrow>
         </msub>
      </math>. Moreover, a larger diaphragm also increases diffraction et and phase effects,
   <li class=itemize>
      the loss term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>
      should be lower. However, if the damping decreases, the bandwidth will be smaller.
</ul>
<p>
   Designing a good microphone requires compromises between all the constraints while choosing the most appropriate materials. For instance, there is a trade-off on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   between bandwidth and sensitivity.
<p>
   When the microphone is no longer connected to a close to infinite load, but for instance to a resistive load
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>, the sensitivity can be obtained from the system of equation (<a href=#x1-2012r8>8</a>)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>The drop in high-frequency response is a very limiting factor of the moving-coil microphone, so it is unlikely to be used where high frequencies need to be picked up. One way of mitigating this (albeit to a limited extent) is to add acoustic resonators to the cavity, tuned to the frequencies where the frequency response begins to be attenuated.</p>
<p>Although the moving coil has limited bandwidth and sensitivity, some of its advantages can be mentioned: (1) they are inexpensive; (2) they are robust for intensive use and adverse weather conditions; (3) it is difficult to achieve saturation or clipping with these microphones. Typical applications include recording vocals outdoors, drums (snare, kick and toms), and certain instruments such as electric bass and guitar with specific microphones such as the very popular Shure SM57.</p>
<h3 class=sectionHead>Ribbon microphone</h3>
<p>
   Let us now focus on the ribbon microphone represented in <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/figure-4.svg data-img-alt="Figure 4. Ribbon microphone principle.">Figure 4</a>. A metallic ribbon of length
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   and surface
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   is placed in a constant magnetic field
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>
   created by two parallel magnets. Note that other magnet and pole piece arrangements are also used, especially for older design. The ribbon is guided by its suspension to vibrate with a velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </mstyle>
   </math>
   orthogonal to the magnetic field
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <mi>B</mi>
      </mstyle>
   </math>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/15/theory/figure-4.svg alt="Figure 4. Ribbon microphone principle."><figcaption class=caption>Figure 4. Ribbon microphone principle.</figcaption></figure>
<p>
   The ribbon is assumed to behave as a 1-DOF system with a mechanical impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Note that we also assume that the front and rear radiation impedances are included in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>. The microphone is connected to a loading resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   on which the output voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   is measured.<br class=newline>Let us consider that a plane wave is exciting the membrane. Since we have a pressure gradient microphone, the force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
   </math>
   due to the incident sound acting on the diaphragm is given by (see <a href="${linkLesson0114}" target="_blank">Lesson 14</a>)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <mfrac>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mi>e</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mi>p</mi>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the sound velocity,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>e</mi>
   </math>
   is the contour distance between the front and right side of the diaphragm,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   is the angle of incidence and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
   </math>
   is the pressure of the plane wave.<br class=newline>We can use the same approach as for the moving coil microphone to obtain the behavioural equations, however the following differences should be noted:
<ul class=itemize1>
   <li class=itemize>
      here, the coil is replaced by a ribbon of impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      there is no acoustic circuit at the back, the interaction between the two sides of the diaphragm is taken into account in
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>F</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>.
</ul>
<p>Therefore, the behavioural equations are given by
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>l</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                           <mi>I</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mi>j</mi>
                           <mfrac>
                              <mrow>
                                 <mi>ω</mi>
                              </mrow>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                           </mfrac>
                           <mi>e</mi>
                           <mi class=qopname>cos</mi>
                           <mo>⁡</mo>
                           <mi>𝜃</mi>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mi>p</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <mi>I</mi>
                        </mtd>
                     </mtr>
                     <mtr></mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(13)
</table>
<p>It is then possible to calculate the sensitivity according to the system of equations (13)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   Using:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mfrac>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mi>e</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
            <mi>ℓ</mi>
         </mrow>
      </mfrac>
   </math>
   yields
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </mfrac>
                  <mi>e</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(15)
</table>
<p>
   For most microphones, the load resistance is chosen to get
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>, then the sensitivity function can be simplified to
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <mi>ω</mi>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi>e</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi>e</mi>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi>j</mi>
                        <mi>ω</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mi>j</mi>
                              <mi>ω</mi>
                              <msub>
                                 <mrow>
                                    <mi>C</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>m</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   A flat response is obtained for a large mass term:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>j</mi>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. Then, the on-axis sensitivity in the bandwidth
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is equal to
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                  </mfrac>
                  <mo fence=true form=prefix>(</mo>
                  <msub>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
               </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mi>ω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
                  <mrow>
                     <mi>j</mi>
                     <mi>ω</mi>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>m</mi>
                        </mrow>
                     </msub>
                  </mrow>
               </mfrac>
                  <mo fence=true form=postfix>)</mo>
               </mrow>
            </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>B</mi>
               <mi>ℓ</mi>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
               </msub>
               <mi>e</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
               <msub>
                  <mrow>
                     <mi>M</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
         </math>
      <td class=eq-no>(17)
</table>
<p>
   The frequency response of a typical ribbon microphone is shown on <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/figure-5.svg data-img-alt="Figure 5. Relative on-axis sensitivity of a ribbon microphone.">Figure 5</a>. It has the shape of a second order high-pass filter.<br class=newline>
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/15/theory/figure-5.svg alt="Figure 5. Relative on-axis sensitivity of a ribbon microphone.">
      <figcaption class=caption>Figure 5. Relative on-axis sensitivity of a ribbon microphone.</figcaption>
   </figure>
<p>To increase the sensitivity, the following parameters can be adjusted:
<ul class=itemize1>
   <li class=itemize>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>B</mi>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ℓ</mi>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
         </msub>
      </math>
      should be large:
      <ul class=itemize2>
         <li class=itemize>
            however, increasing of
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
               <mi>ℓ</mi>
            </math>
            and
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
               <msub>
                  <mrow>
                     <mi>S</mi>
                  </mrow>
                  <mrow>
                     <mi>d</mi>
                  </mrow>
               </msub>
            </math>
            leads to a non uniform pressure field on the membrane at high frequencies,
         <li class=itemize>
            moreover, increasing the width of the ribbon will lower
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
               <mi>B</mi>
            </math>.
      </ul>
   <li class=itemize>
      the mass
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      should be minimal, but this contradicts the conditions
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>j</mi>
         <mi>ω</mi>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>≫</mo>
         <mrow>
            <mo fence=true form=prefix>(</mo>
            <mrow>
               <msub>
                  <mrow>
                     <mi>R</mi>
                  </mrow>
                  <mrow>
                     <mi>m</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-punc>,</mo>
               <mfrac>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
                  <mrow>
                     <mi>j</mi>
                     <mi>ω</mi>
                     <msub>
                        <mrow>
                           <mi>C</mi>
                        </mrow>
                        <mrow>
                           <mi>m</mi>
                        </mrow>
                     </msub>
                  </mrow>
               </mfrac>
            </mrow>
            <mo fence=true form=postfix>)</mo>
         </mrow>
      </math>. The ribbon is often corrugated to increase
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      and add stiffness to prevent off-axis motion,
   <li class=itemize>
      the contour length
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>e</mi>
      </math>
      should be large. However, it should also fulfil
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>k</mi>
         <mi>e</mi>
         <mo class=MathClass-rel>≪</mo>
         <mn>1</mn>
      </math>
      to get a good approximation of the pressure gradient.
</ul>
<p>Even after optimisation, ribbon microphones have very low sensitivity, requiring the use of an electrical transformer to increase the output voltage. Here too, the design of a good ribbon microphone requires compromises. Nevertheless, ribbon microphones generally have a precise transient response with a slight attenuation of high frequencies. Typical uses include recording studio vocals, brass instruments or drum overheads.
    
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, two electrodynamic microphones were modelled to obtain their bandwidth sensitivity and response shape. The results are summarised in <a href=# data-img-src=${config.ASSETS_URL}/04/15/theory/table-1.svg data-img-alt="Table 1. Properties of moving coil and ribbon transduction.">Table 1</a>. Note that these theoretical models were developed using assumptions that are satisfied in the low frequency band (for a wavelength greater than the dimensions of the microphone). As a result, the predicted high-frequency response is generally inaccurate due to the following physical phenomena.
<ul class=itemize1>
   <li class=itemize>diffraction on the microphone body,
   <li class=itemize>phase effects on the diaphragm of off-axis incidence,
   <li class=itemize>membrane modes (the membrane stops to behave as a 1-DOF system),
   <li class=itemize>when the contour distance is equal to the wavelength, the pressure on both sides of the diaphragm is equal, creating a roll-off in the response.
</ul>
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Properties of moving coil and ribbon transduction.</figcaption>
      <img src=${config.ASSETS_URL}/04/15/theory/table-1.svg alt="Table 1. Properties of moving coil and ribbon transduction.">
   </figure>
</div>
<p>Models can be improved to account for these phenomena by adding degrees of freedom or by performing numerical simulations. However, these simple models are easy and fast to calculate, and give good accuracy in sensitivity and low cut-off frequency.<br class=newline>By modifying the rear acoustic load, it is possible to obtain other directivity patterns. This will be investigated in a future Lesson on the modelling of unidirectional microphones.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>1-DOF
  <dd>1 Degree Of Freedom
  <dt>EMF
  <dd>ElectroMotive Force
  <dt>RLC
  <dd>Resistor, Inductor, Capacitor
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Beranek2012 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Leo Beranek and Tim Mellow. <span class=cmti-10>Acoustics: sound fields and transducers</span>. Academic Pres, 2012. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780123914866.
   <dt id=X0-Kleiner2013 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Mendel Kleiner. <span class=cmti-10>Electroacoustics</span>. CRC Press, 2013. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 9780429073519.
   <dt id=X0-Rossi1988 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Mario Rossi. <span class=cmti-10>Acoustics and Electroacoustics</span>. Artech House, 1988. <span class=cmcsc-10><span class=small-caps>i</span><span class=small-caps>s</span><span class=small-caps>b</span><span class=small-caps>n</span></span>: 978-0890062555.
</dl>`;

export default theory;
